'use client';

import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { useServerInsertedHTML } from 'next/navigation';
import { useState } from 'react';

interface InsertedStyle {
  name: string;
  isGlobal: boolean;
}

export const EmotionRegistry: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [{ cache, flush }] = useState(() => {
    const cache = createCache({ key: 'css' });
    cache.compat = true;
    let inserted: InsertedStyle[] = [];
    if (typeof document === 'undefined') {
      const previousInsert = cache.insert;
      cache.insert = (...args) => {
        const [selector, serialized] = args;
        if (cache.inserted[serialized.name] === undefined) {
          inserted.push({ name: serialized.name, isGlobal: !selector });
        }
        return previousInsert(...args);
      };
    }
    return {
      cache,
      flush: () => {
        const styles = inserted;
        inserted = [];
        return styles;
      },
    };
  });

  useServerInsertedHTML(() => {
    const globals: { name: string; css: string }[] = [];
    const names: string[] = [];
    let css = '';
    for (const { name, isGlobal } of flush()) {
      const style = cache.inserted[name];
      if (typeof style !== 'string') continue;
      if (isGlobal) {
        globals.push({ name, css: style });
      } else {
        names.push(name);
        css += style;
      }
    }
    return (
      <>
        {globals.map(({ name, css }) => (
          <style key={name} data-emotion={`${cache.key}-global ${name}`} dangerouslySetInnerHTML={{ __html: css }} />
        ))}
        {names.length > 0 && (
          <style data-emotion={`${cache.key} ${names.join(' ')}`} dangerouslySetInnerHTML={{ __html: css }} />
        )}
      </>
    );
  });

  return <CacheProvider value={cache}>{children}</CacheProvider>;
};
